import { useVirtualizer } from '@tanstack/react-virtual';
import _, { escapeRegExp } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createEditor, Editor, Element, Range, Text, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact } from 'slate-react';
import { useEditorKeyDownHandler } from '../../../../hook/useEditorKeyDownHandler';
import useKeyPress from '../../../../hook/useKeyPress';
import { getUserInfo } from '../../../../utils/UserInfoUtils';
import { checkForceUnclaimRequest, vendorCompleteParagraphRequest } from '../../Editor/actions';
import { saveSlateDataRequest } from '../actions/saveSlateDataActions';
import { setAudioStartTime } from '../actions/settingsActions';
import { updateFlagSlateData, updateSlateData, updateSlateIsDirty } from '../actions/slateDataActions';
import SlateElement from './slateElement';
import { AUTO_SAVE_TIME } from '../../../../constants/Config';
import { handleSlatePaste } from '../../../../utils/handlePasteEditor';
import { trackEvent } from '../../../../utils/ga';

const SlateEditor = forwardRef(({ setCanUndo, setCanRedo, setEditor }, ref) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const {
    data: transcriptData,
    error,
    isUpdated,
    selectedNode,
    scrollToIndexValue,
  } = useSelector((state) => state.slateData);
  let editorDetails = useSelector((state) => state?.editorDetails?.user?.data?.value);
  let editorDetailsData = editorDetails && JSON.parse(editorDetails);
  const allowEdit = editorDetailsData?.Status !== 'Rework';
  const {
    word: searchedWord,
    wordSearchedId,
    mode,
    isMatchFullWord,
    isCaseSensitive,
    isFillerWords,
  } = useSelector((state) => state.findAndReplace);
  const [value, setValue] = useState(transcriptData);
  const [resumeDone, setResumeDone] = useState(false);
  const [resumeIndex, setResumeIndex] = useState(-1);
  const isInitialRender = useRef(true);
  const userInfo = getUserInfo();
  // ref
  const updateRef = useRef({
    pendingOperations: {},
    timeout: null,
    isUpdating: false,
  });
  const parentRef = useRef(null);
  const r = useRef(null);
  const saveTimeoutRef = useRef(null);

  const dataLength = useMemo(() => value?.length || 0, [value?.length]);

  // Memoize the estimateSize function
  const rowVirtualizer = useVirtualizer({
    count: dataLength,
    getScrollElement: useCallback(() => parentRef.current, []),
    estimateSize: () => 45,
    overscan: 5,
    enabled: true,
    getItemKey: useCallback(
      (index) => {
        // Use stable keys to prevent re-rendering issues
        return value[index]?.id || `item-${index}`;
      },
      [value],
    ),
  });
  const virtualItems = useMemo(() => rowVirtualizer.getVirtualItems(), [rowVirtualizer.getVirtualItems()]);

  const handleSave = useCallback(
    (newValue = value) => {
      if (!allowEdit) return;
      if (updateRef.current.saveTimeout) {
        clearTimeout(updateRef.current.saveTimeout);
        updateRef.current.saveTimeout = null;
      }
      dispatch(
        checkForceUnclaimRequest({
          claimId: editorDetailsData?.claimId,
          userId: userInfo?.UserId,
        }),
      );
      dispatch(
        saveSlateDataRequest({
          audioFileId: id,
          transcriptJSON: JSON.stringify({
            data: newValue,
          }),
        }),
      );
      dispatch(
        vendorCompleteParagraphRequest({
          userId: userInfo?.UserId,
          audioFileId: id,
          isVendor: true,
          totalParagraphs: newValue?.length,
          completedParagraphs: newValue?.filter((i) => i.completed)?.length,
        }),
      );
    },
    [value],
  );

  const flushUpdates = useCallback(() => {
    const updates = updateRef.current.pendingOperations;
    updateRef.current.pendingOperations = {};
    updateRef.current.isUpdating = false;

    setValue((prevValue) => {
      const newValue = [...prevValue];
      Object.entries(updates).forEach(([index, node]) => {
        newValue[parseInt(index)] = {
          ...newValue[parseInt(index)],
          ...node,
        };
      });
      return newValue;
    });
  }, []);

  const handleChange = useCallback(
    (newValue) => {
      if (!allowEdit) return;
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }

      if (updateRef.current.saveTimeout) {
        clearTimeout(updateRef.current.saveTimeout);
      }
      updateRef.current.saveTimeout = setTimeout(() => {
        handleSave(newValue);
      }, AUTO_SAVE_TIME);
      const operations = editor.operations;

      // Skip selection-only changes
      if (operations.every((op) => op.type === 'set_selection')) {
        return;
      }

      setCanUndo(editor.history.undos.length > 0);
      setCanRedo(editor.history.redos.length > 0);

      // Process content operations
      operations.forEach((op) => {
        if (op.type !== 'set_selection' && 'path' in op && 'node' in op) {
          const rootIndex = op.path[0];
          updateRef.current.pendingOperations[rootIndex] = op.node;
        }
      });

      // Clear existing timeout
      if (updateRef.current.timeout) {
        clearTimeout(updateRef.current.timeout);
      }

      // Schedule update
      if (!updateRef.current.isUpdating) {
        updateRef.current.isUpdating = true;
        updateRef.current.timeout = setTimeout(flushUpdates, 32); // Approximately 2 frames
      }

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        setValue(newValue);
        dispatch(updateSlateIsDirty(true));
        dispatch(updateSlateData(newValue));
      }, 1000);
    },
    [editor, flushUpdates, setCanUndo, setCanRedo],
  );

  const handleUndo = useCallback(() => {
    if (editor.history.undos.length > 0) {
      editor.undo();
    }
  }, [editor]);

  const handleRedo = useCallback(() => {
    if (editor.history.redos.length > 0) {
      editor.redo();
    }
  }, [editor]);

  useImperativeHandle(ref, () => ({
    handleUndo,
    handleRedo,
  }));

  useKeyPress(handleSave, 's', true);

  useEffect(() => {
    return () => {
      if (updateRef.current.timeout) {
        clearTimeout(updateRef.current.timeout);
      }
    };
  }, []);

  useEffect(() => {
    if (isUpdated && transcriptData && transcriptData.length) {
      setValue(transcriptData);
      handleChange(transcriptData);
      dispatch(updateFlagSlateData(false));
      setResumeIndex(transcriptData.findIndex((item) => !item.completed));
    }
  }, [isUpdated, transcriptData]);

  const renderLeaf = useCallback(
    ({ attributes, children, leaf }) => {
      let result = children;
      if (leaf.bold) result = <strong>{result}</strong>;
      if (leaf.italic) result = <em>{result}</em>;
      if (leaf.underlined) result = <u>{result}</u>;
      const handleClick = (e) => {
        e.preventDefault();
        dispatch(setAudioStartTime(leaf.data?.start));
      };
      // Determine background color based on highlight type
      let backgroundColor = 'transparent';
      if (leaf.highlight) {
        const id = wordSearchedId?.replace(/-\((\d+)\)/, (match, p1) => {
          const number = parseInt(p1, 10);
          if (number === 0) {
            return match; // leave as is
          } else {
            return `-${number - 1}`;
          }
        });
        backgroundColor = id === leaf.matchId ? 'yellow' : 'grey';
      }

      return (
        <span
          onClick={handleClick}
          style={{
            backgroundColor,
          }}
          className={leaf.highlight ? 'highlighted-word' : ''}
          id={leaf.highlight ? `highlighted-word` : ''}
          {...attributes}
        >
          {result}
        </span>
      );
    },
    [wordSearchedId, mode],
  );

  useEffect(() => {
    const indexToScroll = selectedNode !== null && selectedNode !== undefined ? selectedNode : scrollToIndexValue;

    if (indexToScroll !== null && indexToScroll !== undefined) {
      rowVirtualizer.scrollToIndex(indexToScroll);
    }
  }, [selectedNode, scrollToIndexValue, wordSearchedId]);

  useEffect(() => {
    if (resumeIndex !== -1 && !resumeDone) {
      const element = document.getElementById(`element-${resumeIndex}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
        });
      }
      setResumeDone(true);
    }
  }, [resumeIndex]);

  useEffect(() => {
    if (setEditor) setEditor(editor);
  }, [editor, setEditor]);

  const renderVirtualElement = useCallback(
    (props) => {
      const index = editor.children?.findIndex((block) => block.id === props.element.id);
      if (index === undefined || index < 0) return null;

      const virtualItem = virtualItems.find((item) => item.index === index);
      if (!virtualItem) {
        return null;
      }

      if (!virtualItem) {
        return (
          <div data-index={index} className="elem out-of-view flex p-4 " style={{ width: '100% !important' }}>
            {renderElement(props)}
          </div>
        );
      }

      return (
        <div
          id={`element-${index}`}
          key={virtualItem.key}
          data-index={virtualItem.index}
          ref={rowVirtualizer.measureElement}
          className="elem flex "
          style={{ width: '100% !important' }}
        >
          {renderElement(props)}
        </div>
      );
    },
    [virtualItems, editor.children],
  );

  const renderElement = useCallback(
    (props) => <SlateElement {...props} editor={editor} style={{ width: '100%' }} />,
    [],
  );

  const handleKeyDown = useEditorKeyDownHandler(editor);

  const handleEditorBlur = useCallback(() => {
    const latestEditorValue = editor.children;
    dispatch(updateSlateData(latestEditorValue));
  }, [value]);

  const handlePaste = useCallback(
    (event) => {
      event.preventDefault();

      const html = event.clipboardData.getData('text/html')?.trim();
      const plain = event.clipboardData.getData('text/plain')?.trim();

      const nodes = handleSlatePaste(editor, html, plain);

      trackEvent('paste_text_editor', {
        paste_text: html || plain,
      });

      if (editor) {
        Transforms.insertFragment(editor, nodes);
      }
    },
    [editor, handleSlatePaste],
  );

  const handleCopy = useCallback(
    (e) => {
      const { selection } = editor;
      if (!selection || (typeof Range?.isCollapsed === 'function' && Range?.isCollapsed(selection))) {
        return;
      }
      const selectedText = Editor.string(editor, selection);

      trackEvent('copy_text_editor', {
        copy_text: selectedText,
      });

      e.clipboardData.setData('text/plain', selectedText);
      e.preventDefault();
    },
    [editor],
  );

  const decorate = useCallback(
    ([node, path]) => {
      const ranges = [];
      if (!Array.isArray(searchedWord) || searchedWord.length === 0) return ranges;
      if (mode === 'speaker') return ranges;

      // 1. Build combined regex just like extractSentencesWithTargetWord
      const regexFlags = isCaseSensitive ? 'g' : 'gi';
      const wordsToFind = searchedWord;
      const escapedWords = wordsToFind.map((w) => escapeRegExp(w));
      const pattern =
        isMatchFullWord || isFillerWords
          ? `(?<=^|\\s)(${escapedWords.join('|')})(?=\\s|$)`
          : `(${escapedWords.join('|')})`;
      const combinedRegex = new RegExp(pattern, regexFlags);

      // Helper to find wordIndex from the match
      const getWordIndex = (matched) => {
        const norm = isCaseSensitive ? matched : matched.toLowerCase();
        return wordsToFind.findIndex((w) => (isCaseSensitive ? w : w.toLowerCase()) === norm);
      };

      // 2. Element-with-Text-children case (split matches across children)
      if (Element.isElement(node) && Array.isArray(node.children) && node.children.every(Text.isText)) {
        const texts = node.children.map((c) => c.text);
        const flatText = texts.join('');
        const haystack = isCaseSensitive ? flatText : flatText.toLowerCase();

        let match;
        let counter = 0;

        combinedRegex.lastIndex = 0;
        while ((match = combinedRegex.exec(haystack)) !== null) {
          if (!match[0]) break;

          const [fullMatch, wordMatch] = match;
          const start = match.index;
          const length = fullMatch.length;
          const matchId = `${node.id}-${counter++}`;
          const wordIndex = getWordIndex(wordMatch);

          // locate start child and split
          let iter = 0,
            idx = 0;
          while (idx < texts.length && start >= iter + texts[idx].length) {
            iter += texts[idx].length;
            idx++;
          }
          let offset = start - iter;
          let rem = length;
          while (idx < texts.length && rem > 0) {
            const run = Math.min(rem, texts[idx].length - offset);
            const childPath = [...path, idx];
            ranges.push({
              anchor: { path: childPath, offset },
              focus: { path: childPath, offset: offset + run },
              highlight: true,
              index: wordIndex,
              matchId,
            });
            rem -= run;
            iter += texts[idx].length;
            offset = 0;
            idx++;
          }
        }
      }

      return ranges;
    },
    [searchedWord, mode, isMatchFullWord, isFillerWords, isCaseSensitive],
  );

  const editableProps = useMemo(
    () => ({
      id: 'ant-slate',
      ref: r,
      className: 'outline-none editable max-w-[1000px]',
      onKeyDown: handleKeyDown,
      renderElement: renderVirtualElement,
      renderLeaf: renderLeaf,
      onBlur: handleEditorBlur,
      // spellCheck: false,
      onPaste: handlePaste,
      onCopy: handleCopy,
      decorate,
    }),
    [virtualItems, decorate, renderLeaf, wordSearchedId, mode, editor.children],
  );

  if (error) return <p>Error: {error}</p>;
  if (!transcriptData) return null;
  return value?.length > 0 ? (
    <Slate editor={editor} initialValue={value} onValueChange={handleChange}>
      <div
        ref={parentRef}
        style={{
          height: `inherit`,
          width: '100%',
          overflowY: 'auto',
          contain: 'strict',
          willChange: 'transform',
        }}
        className="custom-scroll"
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            <Editable {...editableProps} />
          </div>
        </div>
      </div>
    </Slate>
  ) : null;
});

export default React.memo(SlateEditor, (prev, next) => _.isEqual(prev.data, next.data));
